/** @format */

import {useMemo, useState, useCallback} from 'react';

import type {
  FilterOption,
  UseTableFiltersProps,
  UseTableFiltersReturn,
  TableFilters,
  FilterValue,
} from '../components/FilterBar/types';

export const useTableFilters = ({
  data,
  filterableColumns,
  filterOrder,
}: UseTableFiltersProps): UseTableFiltersReturn => {
  const [filters, setFilters] = useState<TableFilters>({});

  const filterOptions = useMemo(() => {
    const options: Record<string, FilterOption[]> = {};

    filterableColumns.forEach(columnId => {
      const currentIndex = filterOrder.indexOf(columnId);
      const previousFilters: TableFilters = {};

      if (currentIndex !== -1) {
        filterOrder.slice(0, currentIndex).forEach(prevColumnId => {
          if (filters[prevColumnId]) {
            previousFilters[prevColumnId] = filters[prevColumnId];
          }
        });
      }

      let filteredForOptions = data;
      Object.entries(previousFilters).forEach(([filterColumnId, filterValue]) => {
        if (filterValue) {
          filteredForOptions = filteredForOptions.filter(item => {
            const cellValue = item[filterColumnId];
            return String(cellValue) === filterValue;
          });
        }
      });

      const uniqueValues = new Set<string>();
      filteredForOptions.forEach(item => {
        const value = item[columnId];
        if (value != null && value !== '') {
          uniqueValues.add(String(value));
        }
      });

      options[columnId] = Array.from(uniqueValues)
        .sort()
        .map(value => ({
          value,
          label: value,
        }));
    });

    return options;
  }, [data, filterableColumns, filters, filterOrder]);

  const filteredData = useMemo(() => {
    let result = data;

    Object.entries(filters).forEach(([columnId, filterValue]) => {
      if (filterValue) {
        result = result.filter(item => {
          const cellValue = item[columnId];
          return String(cellValue) === filterValue;
        });
      }
    });

    return result;
  }, [data, filters]);

  const handleFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setFilters(prevFilters => {
        const newFilters = {...prevFilters};
        newFilters[columnId] = value;

        const currentIndex = filterOrder.indexOf(columnId);
        if (currentIndex !== -1) {
          filterOrder.slice(currentIndex + 1).forEach(nextColumnId => {
            delete newFilters[nextColumnId];
          });
        }

        return newFilters;
      });
    },
    [filterOrder],
  );

  const handleResetFilters = useCallback(() => {
    setFilters({});
  }, []);

  return {
    filters,
    filteredData,
    filterOptions,
    handleFilterChange,
    handleResetFilters,
  };
};
